const API_BASE = '/api'

function workspacePath(workspaceKey: string, suffix = ''): string {
  if (!workspaceKey) throw new Error('Workspace is required')
  return `/workspaces/${encodeURIComponent(workspaceKey)}${suffix}`
}

async function authFetch(path: string, options?: RequestInit): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', ...options })
  if (res.status === 401) {
    window.location.href = '/auth/login'
    throw new Error('Session expired')
  }
  return res
}

async function failFriendly(res: Response, fallback: string): Promise<never> {
  let msg = fallback
  try {
    const body = await res.json()
    if (body?.error && typeof body.error === 'string') msg = body.error
  } catch {
    void 0
  }
  throw new Error(msg)
}

export interface CronWrite {
  workspace_key: string
  agent_name: string
  frequency: 'daily' | 'weekly' | 'monthly'
  hour: number
  minute: number
  day_of_week?: number | null
  day_of_month?: number | null
  timezone: string
  enabled?: boolean
}

export async function createCron(body: CronWrite): Promise<{ id: string }> {
  const res = await authFetch(workspacePath(body.workspace_key, '/crons'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) await failFriendly(res, "Couldn't create the cron.")
  return res.json()
}

export async function updateCron(id: string, body: CronWrite): Promise<void> {
  const res = await authFetch(workspacePath(body.workspace_key, `/crons/${id}`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) await failFriendly(res, "Couldn't update the cron.")
}

export async function setCronEnabled(workspaceKey: string, id: string, enabled: boolean): Promise<void> {
  const res = await authFetch(workspacePath(workspaceKey, `/crons/${id}/enabled`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  })
  if (!res.ok) await failFriendly(res, "Couldn't update the cron.")
}

export async function deleteCron(workspaceKey: string, id: string): Promise<void> {
  const res = await authFetch(workspacePath(workspaceKey, `/crons/${id}`), { method: 'DELETE' })
  if (!res.ok) await failFriendly(res, "Couldn't delete the cron.")
}

export async function runCronNow(workspaceKey: string, id: string): Promise<void> {
  const res = await authFetch(workspacePath(workspaceKey, `/crons/${id}/run-now`), { method: 'POST' })
  if (!res.ok) await failFriendly(res, "Couldn't start the run.")
}

export async function runAgentNow(name: string, workspaceKey: string): Promise<void> {
  const res = await authFetch(workspacePath(workspaceKey, `/agents/${encodeURIComponent(name)}/run-now`), { method: 'POST' })
  if (!res.ok) await failFriendly(res, "Couldn't start the run.")
}
